import { useEffect, useRef, useState } from "react";

interface UseDebounceOptions {
  /** Fire on the leading edge (immediately on first change after idle). Default: false. */
  leading?: boolean;
}

/**
 * Returns a debounced copy of `value`.
 *
 * By default this is a **trailing-edge** debounce — the value updates only
 * after `delay` ms of silence.  Pass `{ leading: true }` to also fire on the
 * leading edge (first change fires immediately, then coalesces until idle).
 */
export function useDebounce<T>(
  value: T,
  delay: number,
  options: UseDebounceOptions = {},
): [T] {
  const { leading = false } = options;
  const [debouncedValue, setDebouncedValue] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const isIdleRef = useRef(true);

  useEffect(() => {
    if (leading && isIdleRef.current) {
      setDebouncedValue(value);
      isIdleRef.current = false;
    }

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setDebouncedValue(value);
      isIdleRef.current = true;
    }, delay);

    return () => clearTimeout(timerRef.current);
  }, [value, delay, leading]);

  return [debouncedValue];
}
